'use client';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { colors, theme } from '@startengine/design-system.theme.main';
import { css } from '@emotion/react';
import MuiGlobalStyles from '@mui/material/GlobalStyles';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';
import { useSearchParams } from 'next/navigation';

export const NormalGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    width: 524,
    height: 266,
  },
}));

export const dropDownStyles = (selected) => ({
  flexBasis: {
    md: '49% !important',
  },
  '& .MuiInputBase-root': {
    background: colors.gray6,
    borderRadius: '12px',
  },
  '& .MuiSvgIcon-root': {
    color: selected ? '#FDFDFD' : '#000E0B',
  },
  '& .MuiSelect-select > p': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: selected ? colors.softWhite : colors.SEBlack,
  },
});

export const MainSearchBox = styled(Grid)(
  () => `
  align-items: center;
  justify-content: center;
  padding: 0px !important;
  .MuiAutocomplete-root {
    padding-right: 0px;
    @media (min-width: 900px) {
      width: 100%;
    }
  }
  .MuiAutocomplete-root .MuiFormControl-root{
    width: 100%;
  }
  .MuiBox-root {
    width: 100%;
  }
  .MuiBox-root .MuiAutocomplete-root {
    width: 100%;
  }
  `,
);

export const StyledBox = styled(Box)(
  () => `
  .MuiAutocomplete-root {
    padding-right: 0px;
    @media (min-width: 900px) {
      width: 40%;
    }
  }
  .MuiAutocomplete-root .MuiFormControl-root{
    width: 100%;
  }
  .MuiBox-root .MuiAutocomplete-root {
    width: 100%;
  }
  .MuiOutlinedInput-root{
    width: 100%;
  }
  `,
);

export const TopNavBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

export const TopNavText = styled(Typography)(({ theme }) => ({
  fontWeight: '600',
  fontSize: '14px',
  lineHeight: '22px',
  textAlign: 'center',
  letterSpacing: '1px',
  fontFeatureSettings: 'pnum on, lnum on',
  textTransform: 'uppercase',
  color: `${theme.palette.common.midnight}`,
  paddingLeft: '40px',
}));

export const StartupsText = styled(Typography)(({ theme }) => ({
  ...theme.typography.h7,
  [theme.breakpoints.up('md')]: {
    ...theme.typography.h5,
  },
  color: theme.palette.common.dark,
  textAlign: 'left',
}));

export const UsersText = styled(Typography)(({ theme }) => ({
  fontWeight: '600',
  fontSize: '20px',
  lineHeight: '24px',
  letterSpacing: '0.05px',
  color: theme.palette.common.orange,
}));

export const UsersBox = styled(Box)(() => ({
  alignItems: 'center',
  display: 'flex',
}));

export const CarouselTitleText = styled(Typography)(({ theme }) => ({
  fontStyle: 'normal',
  fontWeight: '500',
  fontSize: '32px',
  lineHeight: '36px',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  color: theme.palette.common.charade,
}));

export const CarouselSubtitleText = styled(Typography)(({ theme }) => ({
  fontStyle: 'normal',
  fontWeight: '500',
  fontSize: '16px',
  lineHeight: '21px',
  letterSpacing: '-0.02em',
  color: theme.palette.common.tabsTextColor,
}));

export const DropdownBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  background: '#F7F7F7',
  height: '40px',
  borderRadius: '16px',
  padding: '0 11px',
  marginRight: '15px',
  flexGrow: 1,
  justifyContent: 'space-between',
}));

export const CarouselGrid = styled(Grid)(
  () => `
  > div{
    height: auto;
  }
  div.activeEvent {
    height: 219px;
    span {
      height: 100% !important;
    }
  }
  .MuiTypography-body1{
    min-height: 16px;
  }
  @media (min-width: 600px) {
    div.activeEvent {
      height: auto;
    }  
  }
  @media (max-width: 599px) {
    .outerTombstone:last-child {
      width: 90% !important;
      .MuiBox-root {
        padding-right: 18px !important;
      }
    }
  }
  `,
);

export const BaseTypography = styled(Typography)(({ theme }) => ({
  fontWeight: '500',
  fontSize: '8px',
  lineHeight: '14px',
  letterSpacing: '0.05px',
  color: theme.palette.common.tabsTextColor,
  zIndex: 1,
  [theme.breakpoints.up('sm')]: {
    maxWidth: '62%',
    fontSize: '5px',
    lineHeight: '10px',
    marginBottom: '10px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '6px',
    lineHeight: '12px',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '8px',
    lineHeight: '14px',
    maxWidth: 'initial',
  },
}));

export const DropdownGrid = styled(Grid)(() => ({}));

export const StyledBanner = styled(Grid)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  flexFlow: 'nowrap',
  width: '100%',
  borderRadius: '16px 16px 0 0',
  [theme.breakpoints.up('sm')]: {
    position: 'initial',
    borderRadius: '16px',
  },
  alignItems: 'center',
  background:
    'linear-gradient(103.39deg, rgba(1, 209, 184, 0.1) 5.84%, rgba(224, 96, 4, 0.1) 66.48%), linear-gradient(0deg, #FCF9F5, #FCF9F5)',
  display: 'flex',
  margin: '0 auto',
  maxWidth: '929px',
  zIndex: 1,
}));

export const ScrollBarGrid = styled(Grid)(({ theme }) => ({
  backgroundColor: theme.palette.common.softWhite,
  position: 'sticky',
  top: '66px',
  zIndex: 10,
  paddingTop: '16px',
  [theme.breakpoints.down('md')]: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  '& .MuiTab-root': {
    minHeight: '44px',
    minWidth: '44px',
  },
}));

export const DropShadowBar = styled(Grid)(() => ({
  backgroundColor: 'transparent',
  boxShadow:
    '0px 2px 5px 0px #0000001A, 0px 10px 10px 0px #00000017, 0px 22px 13px 0px #0000000D, 0px 39px 15px 0px #00000003, 0px 60px 17px 0px #00000000',
  height: '1px',
  width: '100%',
}));

export const CustomDropDownStyle = {
  '.MuiMenu-paper': {
    backgroundColor: colors.gray6,
    borderRadius: '16px',
    marginTop: '8px',
    maxHeight: '432px',
    minWidth: '250px !important',

    boxShadow:
      '0px 66px 55px rgba(0, 0, 0, 0.04), 0px 27.5732px 22.9777px rgba(0, 0, 0, 0.0287542), 0px 14.742px 12.285px rgba(0, 0, 0, 0.0238443), 0px 8.26422px 6.88685px rgba(0, 0, 0, 0.02), 0px 4.38907px 3.65756px rgba(0, 0, 0, 0.0161557), 0px 1.82639px 1.52199px rgba(0, 0, 0, 0.0112458)',
    '&::-webkit-scrollbar': {
      width: '10px',
    },
    '&::-webkit-scrollbar-track': {
      background: colors.gray4,
    },
    '&::-webkit-scrollbar-thumb': {
      background: colors.gray3,
      width: '55px',
      height: '8px',
      borderRadius: '12px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: theme.palette.secondary.main,
    },
  },
  '.MuiList-root': {
    paddingRight: '0 !important',
    width: '100% !important',
  },
  '.MuiMenu-list': {
    paddingBottom: 0,
    '.Mui-selected': {
      backgroundColor: 'rgba(0,0,0,0) !important',
      span: {
        backgroundColor: 'rgba(0,0,0,0) !important',
      },
      color: theme.palette.secondary.main,
    },
    '.MuiMenuItem-root': {
      height: '53.69px',
      fontWeight: '500',
      backgroundColor: 'rgba(0,0,0,0) !important',
      '&:hover': {
        backgroundColor: 'rgba(0,0,0,0) !important',
        span: {
          backgroundColor: 'rgba(1, 209, 184, 0.1) !important',
          borderRadius: '16px',
        },
      },
      span: {
        margin: '2.75px 0px',
      },
    },
    '& .MuiMenuItem-gutters': {
      padding: 0,
    },
    '& .menu-item': {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
    '& .DividerContainer': {
      position: 'absolute',
      bottom: 0,
      width: '100%',
      padding: '0 16px',
    },
  },
};

export const GlobalStyles = () => {
  const theme = useTheme();
  const searchParams = useSearchParams();
  const search = searchParams.get('search');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <MuiGlobalStyles
      styles={css`
        @media (min-width: 768px) {
          body {
            background-color: ${search && !isMobile ? '#fdfdfb' : '#fafdfa'} !important;
          }
        }
      `}
    />
  );
};

export const TombstoneGrid = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    overflowX: 'hidden',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    width: '100%',
    overflowX: 'hidden',
  },
}));
